import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    Slider,
    Stepper,
    Step,
    StepLabel,
    Chip,
    CircularProgress,
    Alert
} from '@mui/material';
import { AutoAwesome, Add, Delete } from '@mui/icons-material';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebaseClient';

interface GenerateSchemaDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: (schema: any) => void;
    isGlobalMode?: boolean;
}

const steps = ['Framework Details', 'Skills to Assess', 'Structure'];

export const GenerateSchemaDialog: React.FC<GenerateSchemaDialogProps> = ({
    open,
    onClose,
    onSuccess,
    isGlobalMode = false
}) => {
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Form State
    const [name, setName] = useState('');
    const [currentSkill, setCurrentSkill] = useState('');
    const [skills, setSkills] = useState<string[]>([]);
    const [currentCategory, setCurrentCategory] = useState('');
    const [categories, setCategories] = useState<{ name: string; count: number }[]>([]);
    const [criteriaCount, setCriteriaCount] = useState(3);

    const handleNext = () => {
        if (activeStep === 0 && !name.trim()) {
            setError('Please enter a framework name.');
            return;
        }
        if (activeStep === 1 && skills.length < 1) {
            setError('Please add at least one skill to assess.');
            return;
        }
        if (activeStep === 2 && categories.length < 1) {
            setError('Please add at least one category.');
            return;
        }

        setError('');
        if (activeStep === steps.length - 1) {
            handleGenerate();
        } else {
            setActiveStep((prev) => prev + 1);
        }
    };

    const handleBack = () => {
        setActiveStep((prev) => prev - 1);
        setError('');
    };

    const handleAddSkill = () => {
        if (currentSkill.trim() && !skills.includes(currentSkill.trim())) {
            setSkills([...skills, currentSkill.trim()]);
            setCurrentSkill('');
        }
    };

    const handleDeleteSkill = (skillToDelete: string) => {
        setSkills(skills.filter((skill) => skill !== skillToDelete));
    };

    const handleAddCategory = () => {
        if (currentCategory.trim() && !categories.some(c => c.name === currentCategory.trim())) {
            setCategories([...categories, { name: currentCategory.trim(), count: criteriaCount }]);
            setCurrentCategory('');
        }
    };

    const handleDeleteCategory = (categoryName: string) => {
        setCategories(categories.filter((c) => c.name !== categoryName));
    };

    const handleGenerate = async () => {
        try {
            setLoading(true);
            setError('');

            const generateFn = httpsCallable(functions, 'generateEvaluationSchema');
            const result: any = await generateFn({
                name,
                skills,
                categories: categories.map(c => ({ name: c.name, criteriaCount: c.count }))
            });

            if (result.data.success) {
                onSuccess(result.data.schema);
                handleClose();
            } else {
                throw new Error(result.data.error || 'Failed to generate framework');
            }
        } catch (err: any) {
            console.error('Error generating schema:', err);
            setError(err.message || 'An error occurred while generating the framework.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        onClose();
        // Reset state after a short delay
        setTimeout(() => {
            setActiveStep(0);
            setName('');
            setSkills([]);
            setCategories([]);
            setCriteriaCount(3);
            setError('');
        }, 500);
    };

    return (
        <Dialog open={open} onClose={loading ? undefined : handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AutoAwesome color="primary" />
                Generate Framework with AI
            </DialogTitle>
            <DialogContent dividers>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {loading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                        <CircularProgress size={48} sx={{ mb: 2 }} />
                        <Typography variant="h6" color="primary">
                            Generating Framework...
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            This may take up to 30 seconds.
                        </Typography>
                    </Box>
                ) : (
                    <Box>
                        {activeStep === 0 && (
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Framework Details
                                </Typography>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    Give your evaluation framework a descriptive name.
                                </Typography>
                                <TextField
                                    autoFocus
                                    fullWidth
                                    label="Framework Name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="e.g., Senior Sales Pitch Evaluation"
                                    variant="outlined"
                                    sx={{ mt: 1 }}
                                />
                                {isGlobalMode && (
                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        This framework will be generated as a Global Template for all tenants.
                                    </Alert>
                                )}
                            </Box>
                        )}

                        {activeStep === 1 && (
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Skills to Assess
                                </Typography>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    What specific skills or competencies should this framework evaluate? (Add at least 1)
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Add Skill"
                                        value={currentSkill}
                                        onChange={(e) => setCurrentSkill(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleAddSkill()}
                                        placeholder="e.g., Negotiation, Active Listening"
                                    />
                                    <Button
                                        variant="contained"
                                        onClick={handleAddSkill}
                                        disabled={!currentSkill.trim()}
                                        startIcon={<Add />}
                                    >
                                        Add
                                    </Button>
                                </Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {skills.map((skill) => (
                                        <Chip
                                            key={skill}
                                            label={skill}
                                            onDelete={() => handleDeleteSkill(skill)}
                                            color="primary"
                                            variant="outlined"
                                        />
                                    ))}
                                    {skills.length === 0 && (
                                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                            No skills added yet.
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        )}

                        {activeStep === 2 && (
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Structure & Categories
                                </Typography>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    Define the main categories for evaluation and how many criteria each should have.
                                    (Add at least 1 category)
                                </Typography>

                                <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'flex-start' }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Category Name"
                                        value={currentCategory}
                                        onChange={(e) => setCurrentCategory(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                                        placeholder="e.g., Introduction"
                                    />
                                    <TextField
                                        type="number"
                                        size="small"
                                        label="Criteria Count"
                                        value={criteriaCount}
                                        onChange={(e) => setCriteriaCount(Math.max(1, Math.min(10, parseInt(e.target.value) || 3)))}
                                        inputProps={{ min: 1, max: 10 }}
                                        sx={{ width: 100 }}
                                    />
                                    <Button
                                        variant="contained"
                                        onClick={handleAddCategory}
                                        disabled={!currentCategory.trim()}
                                        startIcon={<Add />}
                                        sx={{ minWidth: 100 }}
                                    >
                                        Add
                                    </Button>
                                </Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {categories.map((category) => (
                                        <Chip
                                            key={category.name}
                                            label={`${category.name} (${category.count})`}
                                            onDelete={() => handleDeleteCategory(category.name)}
                                            color="secondary"
                                            variant="outlined"
                                        />
                                    ))}
                                    {categories.length === 0 && (
                                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                            No categories added yet.
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                {!loading && (
                    <>
                        <Button onClick={handleBack} disabled={activeStep === 0}>
                            Back
                        </Button>
                        <Button
                            onClick={handleNext}
                            variant="contained"
                            disabled={
                                (activeStep === 0 && !name.trim()) ||
                                (activeStep === 1 && skills.length === 0) ||
                                (activeStep === 2 && categories.length === 0)
                            }
                            startIcon={activeStep === steps.length - 1 ? <AutoAwesome /> : undefined}
                        >
                            {activeStep === steps.length - 1 ? 'Generate' : 'Next'}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};
